import { Cell, flexRender } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { cn } from '../../../../utils/cn';
import { useTable } from '../TableContext';
import { isPlainLeftClick } from '../is-plain-left-click';

// Utility type for class generation function
type ClassGenerationParams = {
  isFirst: boolean;
  isLast: boolean;
  isExpanded: boolean;
  hasSubComponent?: boolean;
  isLastRow?: boolean;
};

// Centralized class generation utility
const generateCellClasses = {
  rounded: ({ isFirst, isLast, hasSubComponent }: ClassGenerationParams) => [
    'border-y border-table-border',
    isFirst && 'rounded-l-2xl border-l',
    isLast && 'rounded-r-2xl border-r',
    hasSubComponent && 'border-b-0 rounded-bl-none rounded-br-none',
  ],

  // The frame owns the outer edges, as under 'contained'.
  outlined: ({ hasSubComponent, isLastRow }: ClassGenerationParams) => [
    !isLastRow && 'border-b',
    'border-table-border',
    hasSubComponent && 'border-b-0',
  ],

  nested: ({ hasSubComponent, isLastRow }: ClassGenerationParams) => [
    isLastRow ? 'border-0' : 'border-b',
    'border-table-border',
    hasSubComponent && 'border-b-0',
  ],

  contained: ({ hasSubComponent, isLastRow }: ClassGenerationParams) => [
    !isLastRow && 'border-b',
    'border-table-border',
    hasSubComponent && 'border-b-0',
  ],
};

// Main TableCell component with improved type safety and readability
export function TableCell<T>({
  cell,
  className,
  children,
  cellWrapperClassName,
  displayIndex,
  to,
  linked = true,
  tabbable = true,
}: {
  cell: Cell<T, unknown>;
  className?: string;
  children?: React.ReactNode;
  cellWrapperClassName?: string;
  displayIndex?: number;
  /** The row's route, when the row links somewhere. */
  to?: string;
  /** Whether the cell's content sits inside the row link. A cell with its
   *  own links or controls is not, and gets an empty anchor behind its
   *  content instead, so blank space in it still follows the row. */
  linked?: boolean;
  /** Whether this cell's link is the row's tab stop. */
  tabbable?: boolean;
}) {
  const {
    variant = 'default',
    horizontalDensity = 'default',
    expansion,
    table,
    row,
  } = useTable<T>();
  const cellPadding =
    horizontalDensity === 'compact' ? 'px-3 py-5' : 'px-5 py-5';
  // Zebra parity follows visual display order (post-sort), not cell.row.index.
  const isZebraShaded =
    Boolean(row?.zebra) && displayIndex !== undefined && displayIndex % 2 === 1;
  const isRowClickable = Boolean(to || row?.onClick);
  const wrapsContent = Boolean(to) && linked;
  const linkBehind = Boolean(to) && !linked;
  // With a row handler beside the link, a plain left click is the handler's:
  // preventing the default here stops react-router from navigating, and the
  // click then bubbles to the row. Modified clicks stay with the anchor.
  const suppressNavigation = row?.onClick
    ? (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isPlainLeftClick(event)) {
          event.preventDefault();
        }
      }
    : undefined;

  const hasSubComponent = !!expansion?.enabled;
  const isFirst = cell.column.getIndex() === 0;
  const isLast =
    cell.column.getIndex() === cell.row.getVisibleCells().length - 1;
  // Last row in visual display order (post-sort), like zebra parity above;
  // cell.row.index is the position in data and goes stale once sorted.
  const isLastRow =
    (displayIndex ?? cell.row.index) === table.getRowModel().rows.length - 1;
  const isExpanded = cell.row.getIsExpanded();

  // Determine class generation strategy based on variant
  const variantClassGenerator =
    variant === 'rounded' || variant === 'outlined' || variant === 'contained'
      ? generateCellClasses[variant]
      : generateCellClasses.nested;

  const content =
    children || flexRender(cell.column.columnDef.cell, cell.getContext());

  return (
    <td
      role="cell"
      className={cn(
        wrapsContent ? 'p-0' : cellPadding,
        linkBehind && 'relative',
        'whitespace-nowrap',
        'text-sm text-gray-600 dark:text-gray-400',
        isZebraShaded
          ? variant === 'contained'
            ? 'bg-gray-50 dark:bg-gray-800'
            : 'bg-gray-100 dark:bg-gray-800'
          : 'bg-white dark:bg-gray-900',
        // Hover sits past both zebra shades: darker in light, lighter in dark.
        isRowClickable &&
          'group-hover/row:bg-gray-200 dark:group-hover/row:bg-gray-700',
        'align-top',
        'transition duration-150',
        variantClassGenerator({
          isFirst,
          isLast,
          isExpanded,
          hasSubComponent,
          isLastRow,
        }),
        className,
      )}
    >
      {linkBehind && (
        // Under the content, so the content's own links and controls take
        // the click and the rest of the cell falls through to the row link.
        // When no cell carries the row link, this one is the row's tab stop,
        // named by the row's link label.
        <Link
          to={to!}
          tabIndex={tabbable ? undefined : -1}
          aria-hidden={tabbable ? undefined : 'true'}
          aria-label={tabbable ? row?.linkLabel?.(cell.row) : undefined}
          onClick={suppressNavigation}
          className="absolute inset-0 rounded-[inherit] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-500"
        />
      )}
      {wrapsContent ? (
        // Padding lives on the Link so the cell's content is a native
        // anchor; one tab stop per row (only the first linked cell is
        // tabbable). The Link takes its height from its own content: a
        // percentage height here resolves against the cell's specified
        // height, which Firefox reads as the 1px it would be set to.
        <Link
          to={to!}
          tabIndex={tabbable ? undefined : -1}
          onClick={suppressNavigation}
          className={cn('flex items-center', cellPadding, cellWrapperClassName)}
        >
          {content}
        </Link>
      ) : (
        <div
          className={cn(
            'flex items-center',
            // Above the anchor behind it, and no wider than its content so
            // the blank part of the cell reaches that anchor. Capped at the
            // cell, so long content still wraps rather than widening the table.
            linkBehind && 'relative w-fit max-w-full',
            cellWrapperClassName,
          )}
        >
          {content}
        </div>
      )}
    </td>
  );
}
